import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="33116" className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-primary/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-xs font-semibold uppercase tracking-[0.2em] text-foreground/80 transition-colors hover:text-foreground after:absolute after:bottom-[-4px] after:left-1/2 after:h-[1px] after:w-0 after:bg-primary after:transition-all after:duration-500 after:ease-out hover:after:left-0 hover:after:w-full font-mono">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-8 w-32 bg-primary/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center justify-center gap-8 p-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-medium uppercase tracking-[0.25em] text-foreground transition-colors hover:text-primary font-mono" onClick={onClose}>
          {item.label}
        </Link>)}
      <Button asChild className="mt-8 h-12 bg-primary px-8 text-sm font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90 hover:shadow-md transition-all duration-500 font-mono" onClick={onClose}>
        <Link to="#inquire-for-your-event">Book the Band</Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-700 dark bg-background text-foreground ${isScrolled ? "bg-background/85 backdrop-blur-md border-b border-primary/10 shadow-xl" : "bg-background border-b border-white/5"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-6 md:px-12">
        
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-4 z-50">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          <Button asChild className="h-10 bg-primary px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground transition-all duration-500 hover:bg-primary/90 hover:shadow-md font-mono">
            <Link to="/">Home</Link>
          </Button>
        </div>

        <div className="hidden md:block">
          <MenuProvider menu_id="44">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden md:block">
          <Button asChild className="h-10 bg-primary px-6 text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground transition-all duration-500 hover:bg-primary/90 hover:shadow-md font-mono">
            <Link to="#inquiry-form">Book Now</Link>
          </Button>
        </div>

        <Button variant="ghost" size="icon" className="z-50 md:hidden text-foreground hover:bg-transparent hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>

        <div className={`fixed inset-0 z-40 flex h-screen w-screen flex-col items-center justify-center bg-background/98 backdrop-blur-2xl transition-all duration-700 md:hidden ${isMobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"}`}>
          <MenuProvider menu_id="44">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}